import { useEffect, useRef, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

/**
 * useRoninAuthGate — all state + handlers for the Ronin Box signup/login gate
 * (signup form, debounced postal validation, resend cooldown, submit handlers).
 * Extracted from RoninAuthGate so the component is purely presentational. Behavior unchanged.
 */
export function useRoninAuthGate({ initialMode, onAccessGranted, onClose }) {
  const [mode, setMode] = useState(initialMode);
  const [submitted, setSubmitted] = useState(false);
  const [submittedEmail, setSubmittedEmail] = useState("");
  const [resendCooldown, setResendCooldown] = useState(0);
  const [busy, setBusy] = useState(false);
  const { registerRonin, login } = useAuth();

  // signup state
  const [form, setForm] = useState({
    first_name: "", last_name: "", email: "", phone: "",
    country: "US", zip_code: "",
    password: "", marketing_consent: true, terms_accepted: false,
  });
  const [countries, setCountries] = useState([]);
  const [countryOpen, setCountryOpen] = useState(false);
  // postal validation state: idle | checking | ok | bad
  const [postalState, setPostalState] = useState({ status: "idle", place: "", region: "" });
  const postalDebounceRef = useRef(null);

  // Load countries once
  useEffect(() => {
    api.get("/geo/countries").then(({ data }) => setCountries(data || [])).catch(() => { /* silent */ });
  }, []);

  // Debounced postal validation as the user types
  useEffect(() => {
    if (postalDebounceRef.current) clearTimeout(postalDebounceRef.current);
    if (!form.zip_code || form.zip_code.length < 3) {
      setPostalState({ status: "idle", place: "", region: "" });
      return;
    }
    setPostalState((s) => ({ ...s, status: "checking" }));
    postalDebounceRef.current = setTimeout(async () => {
      try {
        const { data } = await api.get(`/geo/validate-postal?country=${encodeURIComponent(form.country)}&postal=${encodeURIComponent(form.zip_code)}`);
        if (data?.valid) {
          setPostalState({ status: "ok", place: data.place || "", region: data.region_code || data.region || "" });
        } else {
          setPostalState({ status: "bad", place: "", region: "" });
        }
      } catch {
        setPostalState({ status: "idle", place: "", region: "" });
      }
    }, 500);
    return () => { if (postalDebounceRef.current) clearTimeout(postalDebounceRef.current); };
  }, [form.country, form.zip_code]);

  // login state
  const [loginEmail, setLoginEmail] = useState("");
  const [loginPassword, setLoginPassword] = useState("");

  const startResendCooldown = () => {
    setResendCooldown(60);
    const t = setInterval(() => {
      setResendCooldown((s) => {
        if (s <= 1) { clearInterval(t); return 0; }
        return s - 1;
      });
    }, 1000);
  };

  const submitSignup = async (e) => {
    e?.preventDefault?.();
    if (!form.marketing_consent) { toast.error("Email opt-in is required to use Ronin Box for free."); return; }
    if (!form.terms_accepted) { toast.error("Please accept the terms."); return; }
    if (postalState.status !== "ok") {
      toast.error("Pick a country and enter a real postal/ZIP code first.");
      return;
    }
    setBusy(true);
    try {
      await registerRonin(form);
      setSubmittedEmail(form.email);
      setSubmitted(true);
      startResendCooldown();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Signup failed");
    } finally { setBusy(false); }
  };

  const submitLogin = async (e) => {
    e?.preventDefault?.();
    setBusy(true);
    try {
      const data = await login(loginEmail, loginPassword);
      // Check Ronin access immediately
      const { data: acc } = await api.get("/auth/has-ronin-access");
      if (acc?.has_ronin_access) {
        onAccessGranted?.(data);
        onClose?.();
      } else {
        // Logged in but not verified
        setSubmittedEmail(loginEmail);
        setSubmitted(true);
      }
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Login failed");
    } finally { setBusy(false); }
  };

  const resend = async () => {
    if (resendCooldown > 0) return;
    setBusy(true);
    try {
      await api.post("/auth/resend-verification");
      toast.success(`Sent again to ${submittedEmail}`);
      startResendCooldown();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Couldn't resend");
    } finally { setBusy(false); }
  };

  const setField = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  return {
    mode, setMode, submitted, submittedEmail, resendCooldown, busy,
    form, setForm, setField, countries, countryOpen, setCountryOpen, postalState,
    loginEmail, setLoginEmail, loginPassword, setLoginPassword,
    submitSignup, submitLogin, resend,
  };
}
